import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../hooks/useResponsive";
import { useContent } from "../cms";
import InteractiveCard from "./InteractiveCard";
import { colors } from "../styles/colors";
import { Play } from "lucide-react";

// Default YouTube video ID (fallback)
const DEFAULT_YOUTUBE_VIDEO_ID = "lzQ_P2IGBXQ";

// Local poster image (eliminates all third-party requests on initial load)
const LOCAL_POSTER_URL = "/images/video-poster.webp";

/**
 * Extract YouTube video ID from various URL formats or return as-is if already an ID
 * Supports: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
 */
function extractYouTubeId(input) {
  if (!input || typeof input !== 'string') return null;

  // If it's already just an ID (no URL structure), return it
  if (/^[A-Za-z0-9_-]{11}$/.test(input)) {
    return input;
  }

  // Try to extract from URL patterns
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([A-Za-z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

const FeaturedVideo = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { getMedia } = useContent();
  const [showVideo, setShowVideo] = useState(false);

  // Get video ID from CMS or use default
  // CMS can provide full YouTube URL or just the ID
  const rawVideoValue = getMedia('featuredVideo.youtubeUrl', DEFAULT_YOUTUBE_VIDEO_ID);
  const videoId = extractYouTubeId(rawVideoValue) || DEFAULT_YOUTUBE_VIDEO_ID;

  // Privacy-enhanced embed URL (loads only when clicked)
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1&color=white&autoplay=1`;

  const handlePlayClick = useCallback(() => {
    setShowVideo(true);
  }, []);

  return (
    <section
      id="beyond-ordinary-wild"
      data-cms-section="featuredVideo"
      data-cms-page="home"
      className={isMobile ? "py-12" : "py-20"}
      style={{ background: colors.gradients.heroSolid }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={isMobile ? "text-center mb-8" : "text-center mb-12"}>
          <h2
            className={`font-bold text-white mb-4 ${isMobile ? "text-3xl" : "text-4xl md:text-5xl"
              }`}
          >
            {t("featuredVideo.title")}
          </h2>
          <p
            className={`mx-auto leading-relaxed text-gray-300 ${isMobile ? "text-base max-w-xl" : "text-lg max-w-3xl"
              }`}
          >
            {t("featuredVideo.description")}
          </p>
        </div>

        <InteractiveCard
          variant="glass"
          className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-lg shadow-2xl overflow-hidden"
          contentClassName="p-0"
        >
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            {showVideo ? (
              /* YouTube iframe - only loaded after user clicks */
              <iframe
                src={embedUrl}
                title={t("featuredVideo.title")}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 w-full h-full"
                style={{ border: 0, borderRadius: "24px 24px 0 0" }}
              />
            ) : (
              /* Facade: Poster image + Play button - no third-party cookies */
              <>
                <img
                  src={getMedia('featuredVideo.posterUrl', LOCAL_POSTER_URL)}
                  alt={t("featuredVideo.thumbnailAlt", "Video preview: Experience the Arctic wilderness")}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ borderRadius: "24px 24px 0 0" }}
                  loading="lazy"
                />
                <button
                  onClick={handlePlayClick}
                  type="button"
                  className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/30 hover:bg-black/20 transition-colors duration-300 cursor-pointer group focus:outline-none focus:ring-4 focus:ring-cyan-400/50"
                  style={{ borderRadius: "24px 24px 0 0" }}
                  aria-label={t("featuredVideo.playButton", "Play video about Cold Experience")}
                >
                  <div
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 group-focus:scale-110 transition-transform duration-300"
                    aria-hidden="true"
                  >
                    <Play className="w-8 h-8 md:w-10 md:h-10 text-slate-900 ml-1" />
                  </div>
                </button>
              </>
            )}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                borderRadius: "24px 24px 0 0",
                boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.08)",
              }}
            />
          </div>
        </InteractiveCard>
      </div>
    </section>
  );
};

export default FeaturedVideo;
